import React, { useRef, useState } from 'react';
import {
  Home,
  Folders,
  BookUser,
  PencilRuler,
  ChevronRight,
  HelpCircle,
} from 'lucide-react';
import myLogo from '../assets/公司logo.png';

// --- 助手组件：侧边栏导航按钮 ---
function NavItem({ icon: Icon, text, onClick, active, hasSubmenu = false }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-150
        ${
          active
            ? 'bg-gray-700 text-white' // 激活时的样式
            : 'text-gray-300 hover:bg-gray-700 hover:text-white' // 默认样式
        }
      `}
    >
      <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
      <span className="flex-grow">{text}</span>
      {hasSubmenu && <ChevronRight className="w-4 h-4" />}
    </button>
  );
}

// --- 助手组件：侧边栏子导航按钮 ---
function SubNavItem({ text, onClick, active }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center w-full text-left pl-12 pr-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150
        ${
          active
            ? 'text-white' // 激活时的样式
            : 'text-gray-400 hover:bg-gray-700 hover:text-white' // 默认样式
        }
      `}
    >
      <span className="flex-grow">{text}</span>
    </button>
  );
}

// --- 组件 1: 侧边栏 (Sidebar) ---
function Sidebar({ onNavigate, currentPage, demoMode, onToggleDemo }) {
  // 截图中的 "AI 软件工具" 是一个可展开的菜单
  const [toolsOpen, setToolsOpen] = useState(true);
  const secretClicks = useRef([]);
  const isToolActive = ['tools', 'video', 'video-composer', 'demo-showcase', 'storyboard', 'storyboard-result', 'frame-editor', 'comic'].includes(currentPage);

  const handleSecretClick = () => {
    const now = Date.now();
    secretClicks.current = [...secretClicks.current.filter((time) => now - time < 2500), now];
    if (secretClicks.current.length >= 5) {
      secretClicks.current = [];
      onToggleDemo?.();
    }
  };

  return (
    <div className="w-64 bg-[#1a1a1a] h-screen p-4 flex flex-col flex-shrink-0">
      {/* 1. Logo */}
      <button type="button" onClick={handleSecretClick} className="flex items-center px-2 mb-6 text-left select-none">
        {/* 这里用 <img> 标签显示你导入的图片 */}
        <img src={myLogo} alt="Logo" className="w-10 h-10 rounded-full mr-3" />
        
        {/* 把文字改成你想要的 (比如 "创世AI 1.0") */}
        <span className="text-white text-lg font-bold">创世AI 1.0</span>
      </button>

      {/* 2. 主导航 */}
      <nav className="flex-grow space-y-2">
        <NavItem
          icon={Home}
          text="首页"
          active={currentPage === 'home'}
          onClick={() => onNavigate('home')}
        />
        <NavItem
          icon={Folders}
          text="我的创作"
          active={currentPage === 'creations'}
          onClick={() => onNavigate('creations')}
        />
        <NavItem
          icon={BookUser}
          text="新手指南"
          active={currentPage === 'guide'}
          onClick={() => onNavigate('guide')}
        />

        {/* AI 软件工具 (带子菜单) */}
        <div className="pt-2">
          <NavItem
            icon={PencilRuler}
            text="AI 软件工具"
            active={isToolActive}
            hasSubmenu={true}
            onClick={() => setToolsOpen(!toolsOpen)}
          />
          {toolsOpen && (
            <div className="mt-1 space-y-1">
              <SubNavItem
                text="AI工具箱"
                active={currentPage === 'tools'}
                onClick={() => onNavigate('tools')}
              />
              <SubNavItem
                text="AI视频生成"
                active={currentPage === 'video'}
                onClick={() => onNavigate('video')}
              />
              <SubNavItem
                text="AI分镜生成"
                active={currentPage === 'storyboard' || currentPage === 'demo-showcase'}
                onClick={() => onNavigate(demoMode ? 'demo-showcase' : 'storyboard')}
              />
              {demoMode && (
                <SubNavItem
                  text="一键全链路演示"
                  active={currentPage === 'demo-showcase'}
                  onClick={() => onNavigate('demo-showcase')}
                />
              )}
              <SubNavItem
                text="AI漫剧生成"
                active={currentPage === 'comic'}
                onClick={() => onNavigate('comic')}
              />
            </div>
          )}
        </div>
      </nav>

      {/* 3. 底部问卷 */}
      <div className="flex-shrink-0 space-y-2">
        {demoMode && (
          <button onClick={onToggleDemo} className="mb-3 flex w-full items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-left text-xs text-emerald-300">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
            演示模式已开启
          </button>
        )}
        <NavItem
          icon={HelpCircle}
          text="问卷调研"
          active={false}
          onClick={() => {}}
        />
      </div>
    </div>
  );
}

export default Sidebar;
